import type {
  ReactNode,
  ElementType,
  DetailedHTMLProps,
  MouseEventHandler,
  ButtonHTMLAttributes,
} from 'react';
import classNames from 'classnames';
import Text from '@/ui/components/Text';
import {
  CaretDownIcon,
} from '@/ui/icons';

import styles from './Select.module.css';

type SelectProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  kind?: 'default' | 'ghost';
  icon?: ElementType;
  emoji?: string;
  loading?: boolean;
  isActive?: boolean;
  roundness?: 'rounded';
  children?: ReactNode;
  viewBox?: string;
  onClick?: MouseEventHandler;
}

const Select = (props: SelectProps) => {
  const {
    ref,
    kind,
    icon,
    emoji,
    children,
    loading = false,
    isActive = false,
    roundness,
    viewBox,
    className,
    ...rest
  } = props;

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const DefaultIcon = icon;
  const withIcon = icon || emoji;

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const selectClassName = classNames({
    [styles.select]: true,
    [styles[roundness]]: !!roundness,
    [styles.loading]: loading,
    [styles.active]: isActive,
    [styles.ghost]: kind === 'ghost',
    [className]: !!className,
  });

  const iconStyle = {};
  if (
    viewBox
  ) iconStyle['viewBox'] = viewBox;

  // ##########################################################################
  // RENDER
  // ##########################################################################

  const Icon = emoji ? (
    <span className={styles.emoji}>
      <Text
        kind="caption"
        size="large"
      >
        {emoji}
      </Text>
    </span>
  ) : (
    <span className={styles.icon}>
      <DefaultIcon
        color="var(--primary-600)"
        {...iconStyle}
      />
    </span>
  );

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={selectClassName}
    >
      {withIcon && Icon}

      {children}

      <span className={styles.chevron}>
        <CaretDownIcon />
      </span>
    </button>
  );
};

export default Select;
export type { SelectProps };
